import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  BookOpenIcon,
  CalculatorIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { Status } from './types';
import { euro } from './utils';

interface Props {
  status: Status | null;
  onSaved: () => void;
  onMessage: (type: 'info' | 'error', title: string, message: string) => void;
}

const Grundlagen = () => (
  <div className="grid gap-4 lg:grid-cols-3">
    {[
      {
        icon: BookOpenIcon,
        title: 'Was ist eine Anlage?',
        text: 'Ein Gegenstand, den der Verein länger nutzt – zum Beispiel ein Mäher, Computer oder Zaun. Laufende Leistungen wie Pflege oder Reparaturen gehören normalerweise nicht hierher.',
        color: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
      },
      {
        icon: CalculatorIcon,
        title: 'Warum Stückzahl und Nettowert?',
        text: 'Die Wertgrenze gilt für jeden selbstständig nutzbaren Gegenstand. Deshalb können 20 Stühle auf einer großen Rechnung trotzdem einzeln geringwertig sein.',
        color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
      },
      {
        icon: ArrowPathIcon,
        title: 'Was bedeutet Abschreibung?',
        text: 'Der Anschaffungswert wird über die Nutzungsjahre verteilt. Im Verzeichnis siehst du deshalb jedes Jahr, welcher Restwert noch in die Vermögensaufstellung gehört.',
        color: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300'
      }
    ].map((item) => (
      <article key={item.title} className="card border border-gray-200 p-5 dark:border-gray-700">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${item.color}`}><item.icon className="h-5 w-5" /></div>
        <h2 className="mt-4 font-bold text-gray-900 dark:text-white">{item.title}</h2>
        <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{item.text}</p>
      </article>
    ))}
  </div>
);

const AnlagenKnowledge: React.FC<Props> = ({ status, onSaved, onMessage }) => {
  const [cfg, setCfg] = useState<any>(null);
  const [offen, setOffen] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    apiClient.get('/api/anlagen/config')
      .then((response) => setCfg(response.data.data))
      .catch(() => onMessage('error', 'Einstellungen nicht geladen', 'Die Wertgrenzen konnten nicht geladen werden.'));
  }, [onMessage]);

  const speichern = async () => {
    setSpeichert(true);
    try {
      await apiClient.put('/api/anlagen/config', cfg);
      onSaved();
      onMessage('info', 'Einstellungen gespeichert', 'Die neuen Grenzen gelten ab dem nächsten Prüfdurchlauf.');
    } catch (error: any) {
      onMessage('error', 'Einstellungen nicht gespeichert', error.response?.data?.message || 'Unbekannter Fehler.');
    } finally {
      setSpeichert(false);
    }
  };

  const feld = (id: string, label: string, hilfe: string, suffix: string) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="flex items-center gap-2"><input id={id} type="number" step="0.01" value={cfg?.[id] ?? ''} onChange={(event) => setCfg((alt: any) => ({ ...alt, [id]: Number(event.target.value) }))} /><span className="shrink-0 text-sm text-gray-500">{suffix}</span></div>
      <p>{hilfe}</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <Grundlagen />

      <section className="card border border-gray-200 p-5 dark:border-gray-700 sm:p-6">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"><ShieldCheckIcon className="h-5 w-5" /></div>
          <div><h2 className="font-bold text-gray-900 dark:text-white">Was der Assistent macht – und was nicht</h2><p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">Er findet auffällige Ausgaben, rechnet Wertgrenzen und Abschreibungen und dokumentiert eure Antworten. Er entscheidet niemals automatisch und ersetzt keine fachliche Prüfung bei Sonderfällen.</p></div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/20"><p className="flex items-center gap-2 text-sm font-bold text-green-800 dark:text-green-200"><CheckCircleIcon className="h-5 w-5" />Dafür ist er gedacht</p><p className="mt-2 text-sm text-green-900/80 dark:text-green-100/80">Alltägliche Anschaffungen verständlich prüfen und den Restbuchwert für die Vermögensaufstellung führen.</p></div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20"><p className="flex items-center gap-2 text-sm font-bold text-amber-800 dark:text-amber-200"><InformationCircleIcon className="h-5 w-5" />Fachlich prüfen lassen</p><p className="mt-2 text-sm text-amber-900/80 dark:text-amber-100/80">Gebäude, größere Baumaßnahmen, gemischt genutzte Güter, Vorsteuerfälle und unklare Nutzungsdauern.</p></div>
        </div>
      </section>

      <section className="card border border-gray-200 dark:border-gray-700">
        <button className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6" onClick={() => setOffen(!offen)} aria-expanded={offen}>
          <div><p className="text-xs font-bold uppercase tracking-wider text-gray-500">Nur für Verantwortliche</p><h2 className="mt-1 font-bold text-gray-900 dark:text-white">Wertgrenzen und Prüfschwelle</h2><p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Die Standardwerte sollten nur nach fachlicher Prüfung geändert werden.</p></div>
          {offen ? <ChevronUpIcon className="h-5 w-5 text-gray-400" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
        </button>
        {offen && (
          <div className="border-t border-gray-200 p-5 dark:border-gray-700 sm:p-6">
            {!cfg ? <p className="text-sm text-gray-500">Einstellungen werden geladen …</p> : (
              <div className="space-y-6">
                <div className="grid gap-5 md:grid-cols-2">
                  {feld('grenze_sofortaufwand', 'Sofortaufwand bis', 'Bis zu diesem Nettowert wird die Anschaffung sofort als Aufwand behandelt.', '€ netto')}
                  {feld('grenze_gwg', 'GWG-Grenze bis', 'Bis zu diesem Nettowert kann ein Gegenstand als geringwertiges Wirtschaftsgut behandelt werden.', '€ netto')}
                  {feld('bagatellgrenze_inventar', 'Buchungen prüfen ab', 'Ab diesem Nettowert erscheint eine Ausgabe im Prüfassistenten.', '€ netto')}
                  {feld('standard_ust', 'Standard-Umsatzsteuer', 'Vorbelegung für die Netto-Berechnung; pro Buchung änderbar.', '%')}
                </div>
                {status && <p className="rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-900 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-100">Mit den aktuellen Einstellungen werden Ausgaben ab <strong>{euro(status.schwelle_brutto)} brutto</strong> geprüft.</p>}
                <button className="btn primary" disabled={speichert} onClick={speichern}>{speichert ? 'Wird gespeichert …' : 'Einstellungen speichern'}</button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default AnlagenKnowledge;
